/**
 * Rate-cards slice (PX-W6.4) — the org's rate-card rows for the admin
 * surface. Standard `fetchCache` discipline; writes are non-optimistic (an
 * append-only card with server-side overlap refusal has no safe client-side
 * merge): create/close await the server row, then invalidate and refetch the
 * list. `amountMinor` stays an integer end to end — the form converts at its
 * own boundary and money never becomes a float here.
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { rateCardServiceHttp } from '../services/rateCardService.http'
import type { CloseRateCardInput, CreateRateCardInput, RateCard } from '../types'

/** A failed thunk's payload — the i18n-mappable key plus the raw message. */
export interface RatesError {
  /**
   * The SERVER's HTTP status, carried verbatim. Without it a 403 is
   * structurally indistinguishable from a 500 by the time it reaches a page,
   * which is how AC-6.2 shipped unimplemented on two surfaces (merged review
   * C2): they hardcoded a status into `ErrorPanel` and could never branch to
   * `ForbiddenPanel`. `0` means the request never reached the server.
   */
  status: number
  key?: string
  message: string
}

export interface RateCardsState {
  cards: RateCard[]
  cache: FetchCache
  /** Any create/close in flight — disables the form's submit. */
  mutating: boolean
  error: RatesError | null
}

const initialState: RateCardsState = {
  cards: [],
  cache: { ...initialFetchCache },
  mutating: false,
  error: null
}

const toRatesError = (error: unknown): RatesError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

/** Load the org's cards — exactly once per invalidation. */
export const fetchRateCards = createAsyncThunk<RateCard[], void, { rejectValue: RatesError }>(
  'rateCards/fetchRateCards',
  async (_, { rejectWithValue }) => {
    try {
      return await rateCardServiceHttp.list()
    } catch (error) {
      return rejectWithValue(toRatesError(error))
    }
  },
  {
    condition: (_, { getState }) => {
      const { rateCards } = getState() as { rateCards: RateCardsState }
      return shouldFetch(rateCards.cache, null)
    }
  }
)

/** Create a row, then refetch the list (server truth, overlap refusals included). */
export const createRateCard = createAsyncThunk<
  RateCard,
  CreateRateCardInput,
  { rejectValue: RatesError }
>('rateCards/createRateCard', async (input, { dispatch, rejectWithValue }) => {
  try {
    const card = await rateCardServiceHttp.create(input)
    dispatch(rateCardsMutated())
    await dispatch(fetchRateCards())
    return card
  } catch (error) {
    return rejectWithValue(toRatesError(error))
  }
})

/** Close a row (required exclusive `validTo`), then refetch the list. */
export const closeRateCard = createAsyncThunk<
  RateCard,
  { cardId: string; input: CloseRateCardInput },
  { rejectValue: RatesError }
>('rateCards/closeRateCard', async ({ cardId, input }, { dispatch, rejectWithValue }) => {
  try {
    const card = await rateCardServiceHttp.close(cardId, input)
    dispatch(rateCardsMutated())
    await dispatch(fetchRateCards())
    return card
  } catch (error) {
    return rejectWithValue(toRatesError(error))
  }
})

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const rateCardsSlice = createSlice({
  name: 'rateCards',
  initialState,
  reducers: {
    /** A write landed — re-arm the cache (the thunk refetches right after). */
    rateCardsMutated(state) {
      invalidate(state.cache)
    },
    /**
     * Dismiss a surfaced error and RE-ARM the cache — the fetchCache
     * contract's Retry path ("the error UI rearms it via invalidate"); see
     * the hours slice's twin for the dead-Retry regression this prevents.
     */
    rateCardsErrorDismissed(state) {
      state.error = null
      invalidate(state.cache)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchRateCards.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(fetchRateCards.fulfilled, (state, action) => {
        state.cards = action.payload ?? []
        fetchSucceeded(state.cache, null)
      })
      .addCase(fetchRateCards.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      .addCase(createRateCard.pending, (state) => {
        state.mutating = true
        state.error = null
      })
      .addCase(createRateCard.fulfilled, (state) => {
        state.mutating = false
      })
      .addCase(createRateCard.rejected, (state, action) => {
        state.mutating = false
        state.error = action.payload ?? null
      })
      .addCase(closeRateCard.pending, (state) => {
        state.mutating = true
        state.error = null
      })
      .addCase(closeRateCard.fulfilled, (state) => {
        state.mutating = false
      })
      .addCase(closeRateCard.rejected, (state, action) => {
        state.mutating = false
        state.error = action.payload ?? null
      })
  }
})

export const { rateCardsMutated, rateCardsErrorDismissed } = rateCardsSlice.actions
